/**
 * PD-экран и возрастной вход (PWA-гейт, DoD-11 / W3): verbatim-тексты ТОЛЬКО
 * с сервера (`GET /me/pd-consent`). Клиент ничего не сочиняет и не переводит.
 *
 * Возрастной вход (state="age_gate"): кнопки приходят с сервера (actions с
 * action_id); состояния selected/pending/blocked/declined/ошибка/retry.
 * onDone — ТОЛЬКО при accepted/allowed=true из ответа; HTTP 200 с ok=false или
 * accepted=false НЕ закрывает экран. Повторный GET сохраняет актуальный экран
 * (выбор хранится на сервере — переживает перезагрузку).
 *
 * Безопасность DOM: только textContent/экранирование, НЕ сырой innerHTML из
 * строк API. Совместимость старого выключенного режима (soft/pass) сохранена.
 */

import {
  decideAgeAction,
  decidePdConsent,
  getPdConsent,
  ApiError,
  type PdConsentState,
  type AgeGateAction,
} from "./api";
import { getLang, t } from "./i18n";

/** null = гейт не нужен (soft/pass); иначе — данные экрана. */
export async function fetchPdGate(): Promise<PdConsentState | null> {
  const state = await getPdConsent(getLang());
  return state.state === "hard_first" ||
    state.state === "hard_repeat" ||
    state.state === "age_gate"
    ? state
    : null;
}

/** Построение DOM без innerHTML для пользовательских/серверных строк. */
function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text; // экранирует автоматически
  return node;
}

function screenShell(root: HTMLElement): HTMLElement {
  const wrap = el("div", "pdgate");
  const card = el("div", "pdgate__card");
  wrap.appendChild(card);
  root.appendChild(wrap);
  return card;
}

export function renderPdGate(root: HTMLElement, onDone: () => void): void {
  const render = async (): Promise<void> => {
    root.replaceChildren();
    const loading = screenShell(root);
    loading.appendChild(el("div", "pdgate__loading", t("pd.loading")));

    let data: PdConsentState;
    try {
      data = await getPdConsent(getLang());
    } catch {
      renderError(root, render);
      return;
    }

    if (data.state === "age_gate") {
      renderAgeGate(root, data, onDone, render);
      return;
    }
    renderLegacyPd(root, data, onDone, render);
  };

  void render();
}

function renderError(root: HTMLElement, retry: () => void): void {
  root.replaceChildren();
  const card = screenShell(root);
  card.appendChild(el("p", "pdgate__text", t("pd.error")));
  const btn = el("button", "pdgate__accept", t("pd.retry"));
  btn.addEventListener("click", () => void retry());
  card.appendChild(btn);
}

// ── Возрастной вход (W3) ─────────────────────────────────────────────────────

function renderAgeGate(
  root: HTMLElement,
  data: PdConsentState,
  onDone: () => void,
  retry: () => void,
): void {
  const draw = (d: PdConsentState): void => {
    root.replaceChildren();
    const card = screenShell(root);
    card.appendChild(el("h1", "pdgate__title", t("app.title")));
    card.appendChild(el("p", "pdgate__text", d.texts?.message ?? ""));

    // Подписи состояния (selected/pending/blocked) — сервер уже включил в текст;
    // здесь только кнопки из actions (только серверные).
    for (const a of d.actions ?? []) {
      const btn = el("button", ageBtnClass(a), a.label);
      btn.dataset.actionId = a.action_id;
      btn.addEventListener("click", () => void onAction(a));
      card.appendChild(btn);
    }
  };

  const onAction = async (a: AgeGateAction): Promise<void> => {
    const buttons = Array.from(
      root.querySelectorAll<HTMLButtonElement>("button[data-action-id]"),
    );
    buttons.forEach((b) => (b.disabled = true));
    try {
      const res = await decideAgeAction(a.action_id, getLang());
      // onDone ТОЛЬКО при allowed=true/accepted=true из ответа.
      if (res.allowed === true || res.accepted === true) {
        onDone();
        return;
      }
      // HTTP 200, но ok=false/accepted=false → экран остаётся, показать
      // актуальное состояние (state/action могли измениться).
      if (res.state === "age_gate") {
        draw({
          ...data,
          texts: { message: res.texts?.message ?? data.texts?.message ?? "" },
          actions: res.actions ?? [],
          nonce: res.nonce ?? null,
          status: res.status ?? null,
        });
      } else {
        draw({ ...data, actions: res.actions ?? [] });
      }
    } catch (err) {
      // Ошибка возвращает доступные кнопки (retry) — экран не закрывается.
      if (err instanceof ApiError && (err.status === 403 || err.status === 409)) {
        // Действие отвергнуто сервером (stale/closed) — перечитать экран.
        void retry();
        return;
      }
      renderError(root, retry);
    }
  };

  draw(data);
}

function ageBtnClass(a: AgeGateAction): string {
  if (a.kind === "accept" || a.group_id === "adult_18_plus") return "pdgate__accept";
  if (a.kind === "decline") return "pdgate__decline";
  if (a.kind === "correct") return "pdgate__correct";
  return "pdgate__select";
}

// ── Legacy PD-экран (старый выключенный режим) ───────────────────────────────

function renderLegacyPd(
  root: HTMLElement,
  data: PdConsentState,
  onDone: () => void,
  retry: () => void,
): void {
  root.replaceChildren();
  const card = screenShell(root);
  card.appendChild(el("h1", "pdgate__title", t("app.title")));
  const msg = el("p", "pdgate__text", data.texts?.message ?? "");
  card.appendChild(msg);
  const acceptBtn = el("button", "pdgate__accept", data.texts?.button ?? "");
  const declineBtn = el("button", "pdgate__decline", data.texts?.decline_button ?? "");
  card.appendChild(acceptBtn);
  card.appendChild(declineBtn);

  acceptBtn.addEventListener("click", () => {
    acceptBtn.disabled = true;
    declineBtn.disabled = true;
    void (async () => {
      try {
        const res = await decidePdConsent("accept", getLang());
        // onDone ТОЛЬКО при accepted=true.
        if (res.accepted === true) {
          onDone();
          return;
        }
        acceptBtn.disabled = false;
        declineBtn.disabled = false;
        msg.textContent = t("pd.error");
      } catch {
        acceptBtn.disabled = false;
        declineBtn.disabled = false;
        msg.textContent = t("pd.error");
      }
    })();
  });

  declineBtn.addEventListener("click", () => {
    acceptBtn.disabled = true;
    declineBtn.disabled = true;
    void (async () => {
      let declinedText = t("pd.declined");
      try {
        const res = await decidePdConsent("decline", getLang());
        if (res.declined_text) declinedText = res.declined_text;
      } catch (err) {
        if (err instanceof ApiError && err.status !== 400) {
          declinedText = t("pd.error");
        }
      }
      root.replaceChildren();
      const c2 = screenShell(root);
      c2.appendChild(el("p", "pdgate__text", declinedText));
      const b2 = el("button", "pdgate__accept", t("pd.retry"));
      b2.addEventListener("click", () => void retry());
      c2.appendChild(b2);
      })();
  });
}
